import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import {
  Building2,
  Users,
  Wallet,
  Handshake,
  FileText,
  Image,
  Newspaper,
  TrendingUp,
  Eye,
  Download
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, hasPermission } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [portfolio, team, investors, partners, blog, media, news] = await Promise.all([
          hasPermission('portfolio') ? axios.get(`${API}/admin/portfolio`, { withCredentials: true }) : null,
          hasPermission('team') ? axios.get(`${API}/admin/team`, { withCredentials: true }) : null,
          hasPermission('investors') ? axios.get(`${API}/admin/investors`, { withCredentials: true }) : null,
          hasPermission('partners') ? axios.get(`${API}/admin/partners`, { withCredentials: true }) : null,
          hasPermission('blog') ? axios.get(`${API}/admin/blog`, { withCredentials: true }) : null,
          hasPermission('media') ? axios.get(`${API}/admin/media`, { withCredentials: true }) : null,
          hasPermission('news') ? axios.get(`${API}/admin/news`, { withCredentials: true }) : null,
        ]);

        setStats({
          portfolio: portfolio?.data?.length || 0,
          team: team?.data?.length || 0,
          investors: investors?.data?.length || 0,
          partners: partners?.data?.length || 0,
          blog: blog?.data?.length || 0,
          media: media?.data?.length || 0,
          news: news?.data?.length || 0,
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [hasPermission]);

  const statCards = [
    { label: 'Portfolio Companies', value: stats?.portfolio, icon: Building2, permission: 'portfolio', color: 'bg-blue-500' },
    { label: 'Team Members', value: stats?.team, icon: Users, permission: 'team', color: 'bg-green-500' },
    { label: 'Investors', value: stats?.investors, icon: Wallet, permission: 'investors', color: 'bg-purple-500' },
    { label: 'Partners', value: stats?.partners, icon: Handshake, permission: 'partners', color: 'bg-orange-500' },
    { label: 'Blog Posts', value: stats?.blog, icon: FileText, permission: 'blog', color: 'bg-pink-500' },
    { label: 'Media Resources', value: stats?.media, icon: Image, permission: 'media', color: 'bg-cyan-500' },
    { label: 'News Articles', value: stats?.news, icon: Newspaper, permission: 'news', color: 'bg-amber-500' },
  ];

  const filteredCards = statCards.filter(card => hasPermission(card.permission));

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#211F1F]">Welcome back, {user?.name?.split(' ')[0]}</h1>
        <p className="text-gray-500 mt-1">Here's what's happening with your content today.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {filteredCards.map((card) => (
          <Card key={card.label} className="hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{card.label}</p>
                  <p className="text-3xl font-bold text-[#211F1F] mt-1">
                    {loading ? '-' : card.value}
                  </p>
                </div>
                <div className={`${card.color} p-3 rounded-xl`}>
                  <card.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {hasPermission('portfolio') && (
              <a
                href="/admin/portfolio/new"
                className="p-4 border rounded-lg hover:bg-gray-50 transition-colors text-center"
              >
                <Building2 className="w-8 h-8 mx-auto text-[#C8473A] mb-2" />
                <p className="font-medium">Add Company</p>
              </a>
            )}
            {hasPermission('team') && (
              <a
                href="/admin/team/new"
                className="p-4 border rounded-lg hover:bg-gray-50 transition-colors text-center"
              >
                <Users className="w-8 h-8 mx-auto text-[#C8473A] mb-2" />
                <p className="font-medium">Add Team Member</p>
              </a>
            )}
            {hasPermission('blog') && (
              <a
                href="/admin/blog/new"
                className="p-4 border rounded-lg hover:bg-gray-50 transition-colors text-center"
              >
                <FileText className="w-8 h-8 mx-auto text-[#C8473A] mb-2" />
                <p className="font-medium">Write Blog Post</p>
              </a>
            )}
            {hasPermission('news') && (
              <a
                href="/admin/news/new"
                className="p-4 border rounded-lg hover:bg-gray-50 transition-colors text-center"
              >
                <Newspaper className="w-8 h-8 mx-auto text-[#C8473A] mb-2" />
                <p className="font-medium">Add News</p>
              </a>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
